/**
 * Getting Started guides — one short path per role.
 *
 * Each step says what to do and where, in a sentence or three, and links to
 * the FAQ answers that go deeper (`faq` holds FAQ entry ids). The same
 * content rules as the FAQ apply and are enforced by the same test: no
 * internal names, no roadmap ids, no history.
 */
import type { Role } from '../../api/users';

export interface GuideStep {
  title: string;
  body: string;
  /** FAQ entry ids to link as "Learn more". */
  faq?: string[];
}

export interface Guide {
  role: Role;
  title: string;
  intro: string;
  steps: GuideStep[];
}

export const GUIDES: Guide[] = [
  {
    role: 'participant',
    title: 'Participant',
    intro:
      'You are recorded during training or cases and see your own results — and the cases you took part in — here.',
    steps: [
      {
        title: 'Accept your invitation',
        body: 'Click the link in your invitation email and choose a password. On your first sign-in, add your **job title** and **years of experience** on your Profile — they decide which reference group your results are compared with.',
        faq: ['invitations', 'job-title-experience'],
      },
      {
        title: 'Find your sessions',
        body: 'Your **Dashboard** lists every recording made for you, newest first, and opens the latest one. A dot on a session means its data is still on the way in; the steps under the Cognitive trace title show how far it has come.',
        faq: ['where-is-my-data', 'ready-email'],
      },
      {
        title: 'Read a session',
        body: 'The **Cognitive trace** shows your focus through the recording, with the video and transcript above it — click any moment to see and hear it. Underneath, the **Session metrics** scorecard places you against reference groups, and the **AI Coach** suggests what to work on next.',
        faq: ['cognitive-trace', 'moment-video', 'scorecard', 'ai-coach'],
      },
      {
        title: 'Rate your case',
        body: 'When a recording stops, the capture app offers three quick questions about the case. Your answers appear beside the recording as **Self-assessment**.',
        faq: ['self-assessment'],
      },
      {
        title: 'Watch live and compare with your team',
        body: '**Live** shows the case you are recording in right now, with your team overlaid. **Analysis** replays whole cases you took part in, and its **Benchmark** tab compares you with the reference groups.',
        faq: ['live-participant', 'analysis-participant'],
      },
      {
        title: 'Keep a copy',
        body: '**Download report** on a session\'s scorecard saves it as a PDF; **Longitudinal reports** show your progress over time. **Export my data** on the Dashboard saves all your scores.',
        faq: ['pdf-report', 'longitudinal', 'export-my-data'],
      },
    ],
  },
  {
    role: 'coordinator',
    title: 'Coordinator',
    intro:
      'You run the day-to-day: capture phones, participants, recordings and cases. Tenant settings and staff stay with your tenant admins.',
    steps: [
      {
        title: 'Set up the capture phones',
        body: 'On **Devices**, click **Enroll device** and scan the code with the capture app. Before a case, **Test Sensor Rates** in the app confirms every sensor is streaming well.',
        faq: ['install-capture-app', 'enroll-device', 'rate-check', 'remembered-sensors'],
      },
      {
        title: 'Add participants',
        body: 'On **Participants**, invite people by email — one at a time or in bulk — or add them directly if your organisation uses direct entry.',
        faq: ['invite-participants', 'direct-entry', 'pending-invites', 'participant-status'],
      },
      {
        title: 'Check the cameras',
        body: 'If the room has an edge appliance, use **Test all cameras** on it (or **Live check** on a single camera) before the case to confirm every angle.',
        faq: ['what-is-appliance', 'live-check'],
      },
      {
        title: 'Follow a recording',
        body: 'During the case, **Live** shows everyone recording, overlaid. Afterwards, the steps under a session\'s Cognitive trace show its sensor data, video, scoring and transcript arriving — anything amber or red says what needs attention.',
        faq: ['live-staff', 'where-is-my-data', 'missing-video'],
      },
      {
        title: 'Keep recordings tidy',
        body: 'On **Activities**, right-click a recording to split it, tag its department and facility, attach video, or disable a false start. Group a team\'s recordings of one event under **Procedures → Instances**.',
        faq: ['find-recording', 'split-recording', 'tag-recording', 'procedure-instances', 'disable-recording'],
      },
      {
        title: 'Enroll at a conference booth',
        body: 'On **Participants**, **Enroll at booth** shows a QR code visitors scan to enroll on their own phone. At the simulation station, **Post survey** shows the code they scan afterwards to answer the post survey about their recording.',
        faq: ['booth-self-enroll', 'sim-post-survey-phone', 'participant-status'],
      },
      {
        title: 'Debrief with the participant',
        body: 'While you review a session together, **Record debrief** records the conversation: the room cameras record the picture, your laptop the sound. It is added to the session as a coaching recording and transcribed.',
        faq: ['record-debrief', 'coaching-sessions'],
      },
      {
        title: 'Review and share',
        body: 'Open a participant on the **Dashboard** for their sessions, use **Analysis** to compare a whole team on one case, and download any session as a PDF.',
        faq: ['analysis', 'benchmark-staff', 'pdf-report'],
      },
    ],
  },
  {
    role: 'tenant_admin',
    title: 'Tenant admin',
    intro:
      'You run your organisation on 10NEWTONS: its people, its capture setup, its recordings and how results are presented.',
    steps: [
      {
        title: 'Bring in your team',
        body: 'In **Settings → Admins**, invite other admins, coordinators or read-only observers. Then invite participants on **Participants**.',
        faq: ['invite-admins', 'read-only-admin', 'invite-participants'],
      },
      {
        title: 'Set up capture',
        body: 'Enroll the capture phones on **Devices** and set each one\'s default department and facility. Create your procedures and your **Departments & Facilities**, and set minimum sensor rates in **Settings → Capture quality**.',
        faq: ['enroll-device', 'device-defaults', 'procedures', 'departments', 'minimum-rates'],
      },
      {
        title: 'Add cameras (optional)',
        body: 'For video, install an edge appliance from **Cameras → Add appliance** — the download connects itself — then add each camera and check the view.',
        faq: ['what-is-appliance', 'install-appliance', 'add-cameras'],
      },
      {
        title: 'Follow recordings as they happen',
        body: '**Live** shows every case being recorded. After each case, a session\'s steps show its data arriving and flag anything that needs a look; **Data integrity** lists anything that never arrived.',
        faq: ['live-staff', 'where-is-my-data', 'data-integrity'],
      },
      {
        title: 'Run a conference booth',
        body: 'In **Settings → Conference intake**, set up the **Consent** form and the **Pre survey** for the booth and choose a **Post survey** for the capture app or the simulation station — from templates, from scratch, or copied from another organisation. Then switch intake on.',
        faq: ['conference-intake', 'copy-intake-form', 'post-surveys', 'booth-self-enroll'],
      },
      {
        title: 'Review results',
        body: 'Pick a participant on the **Dashboard** to open their sessions, use **Analysis** for whole-team cases and **Benchmark** comparisons, and download session or progress reports as PDFs. **Record debrief** on a session records the conversation with the participant.',
        faq: ['analysis', 'benchmark-staff', 'pdf-report', 'longitudinal', 'record-debrief'],
      },
      {
        title: 'Keep the data clean',
        body: 'On **Activities**, split recordings that hold two procedures, tag them, and disable false starts — nothing is ever deleted.',
        faq: ['split-recording', 'tag-recording', 'disable-recording'],
      },
      {
        title: 'Tailor what people see',
        body: 'Choose the scorecard metrics in **Settings → Metrics**, turn Benchmark on for staff or participants in **Settings → Compare**, let the AI Coach use coaching debriefs there too, create reference values in **Settings → Cohort baseline**, and choose the time zone in **Settings → Tenant**.',
        faq: ['metric-visibility', 'compare-settings', 'ai-coach-debrief', 'cohort-baseline', 'time-zone'],
      },
      {
        title: 'Secure sign-in',
        body: 'Two-factor sign-in for admins is set in **Settings → Security**; single sign-on status is in **Settings → Single sign-on**.',
        faq: ['tenant-mfa', 'tenant-sso-check'],
      },
    ],
  },
  {
    role: 'superadmin',
    title: '10NEWTONS administrator',
    intro:
      'You set up and support tenants and run the platform. Your tools sit at the bottom of the sidebar.',
    steps: [
      {
        title: 'Create and manage tenants',
        body: 'On **Tenants**, create a tenant with its first admin\'s email and an optional trial license. The Active switch suspends a tenant; deleting hides it but keeps its data.',
        faq: ['tenant-cards', 'first-admin', 'trials', 'disable-delete-tenant'],
      },
      {
        title: 'Issue licenses',
        body: 'On **Licenses**, issue each organisation its license — a term, and optionally participant and device limits — renew it before it ends, and put organisations that work under another one on a sub-license. The **Tenants** list shows every tenant\'s license status.',
        faq: ['issue-license', 'tenant-license-status', 'trials'],
      },
      {
        title: 'Manage access',
        body: '**Access → Platform admins** invites and suspends 10NEWTONS administrators; **Access → Access requests** is where you let in people who signed up on their own.',
        faq: ['platform-admins', 'access-requests', 'reset-alert'],
      },
      {
        title: 'Configure the platform',
        body: '**Configuration** holds Mail, the MFA policy, AI / Insights (transcripts and the AI Coach) and Enterprise SSO for each tenant.',
        faq: ['mail-settings', 'platform-mfa', 'ai-insights', 'sso-setup'],
      },
      {
        title: 'Look after reference values',
        body: '**Library → Catalogue** holds the reference values every scorecard uses; **Library → Submissions** is the review queue for tenants\' own.',
        faq: ['master-baselines'],
      },
      {
        title: 'Keep it running',
        body: '**Operations** shows system status, worker throughput, capture-app releases, every device across tenants and data integrity. **Diagnostics** has crash reports, system logs and the audit trail.',
        faq: ['system-status', 'fleet-devices', 'crash-reports', 'logs-vs-audit'],
      },
    ],
  },
];
